import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Client, Contact, getObjectId, Id, SavedLocation } from '@hero/schema';
import { SavedLocationCreateAPI, savedLocationCreateAPISchema } from '@hero/api';
import { convertToLatLng } from '@hero/core';
import { extractGoogleMapsUrl } from '@hero/agent';
import { useSavedLocationResolveGoogleUrlApi } from '@hero/api-hooks';
import type { MapLocationData } from '@hero/web-components';

const schema = savedLocationCreateAPISchema
  .extend({
    locationType: z.enum(['own', 'client']),
  })
  .superRefine((data, ctx) => {
    if (!data.contacts || data.contacts.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'array',
        inclusive: true,
        path: ['contacts'],
        message: 'Please select a contact',
      });
    }
  });

export type SavedLocationFormValues = z.infer<typeof schema>;

export interface UseSavedLocationFormOptions {
  /** Initial values (e.g. pre-fill from context). Overwritten when setting an item via setSavedLocation. */
  defaultValues?: Partial<SavedLocationFormValues>;
  onSubmit?: (values: SavedLocationFormValues) => void;
  onValidationChange?: (isValid: boolean) => void;
  /**
   * When set, the location type is locked to this value AND the
   * matching counterparty is enforced — see {@link UseSavedLocationFormOptions.forceClientId}.
   *
   * - `'own'`    — own-branch only; the client selector is hidden and `clientId` is cleared.
   * - `'client'` — third-party Client branch only; the type selector is hidden;
   *                the caller MUST pass `forceClientId` (or `defaultValues.clientId`).
   *
   * Used by Client CRM screens (e.g. opening the saved-location modal
   * from a client's workspace) so the user can't pick "my place"
   * accidentally. Mirrors the agreement/order patterns.
   */
  forceType?: 'own' | 'client';
  /**
   * Force the client this saved location belongs to. Used together
   * with `forceType='client'` so the client selector stays disabled
   * and pre-filled.
   */
  forceClientId?: Id;
  /**
   * When `true`, calling {@link setSavedLocation} auto-locks the
   * type selector and (for client records) the client selector to
   * the loaded record's values. Used by edit-mode flows so users can
   * only change contact + map pin + name, never re-target the record
   * to a different client or flip it from client to own (those would
   * effectively be a different SavedLocation). Independent from the
   * caller-supplied {@link forceType} so create flows stay unaffected.
   */
  lockOnLoad?: boolean;
}

function savedLocationToFormValues(savedLocation: SavedLocation): SavedLocationFormValues {
  return {
    location: convertToLatLng(savedLocation.address.location),
    locationType: savedLocation.client ? 'client' : 'own',
    clientId: savedLocation.client?._id,
    contacts: savedLocation.contacts.map(c => ({ _id: c._id })),
    name: savedLocation.name,
    locationUrl: savedLocation.locationUrl,
    // Existing rows pre-isAccurate are treated as exact (`true`) by
    // the schema default; surface that to the form so the toggle
    // reflects the underlying record correctly.
    isAccurate: savedLocation.isAccurate ?? true,
  } as SavedLocationFormValues;
}

export const useSavedLocationForm = (options?: UseSavedLocationFormOptions) => {
  const {
    defaultValues: defaultValuesProp,
    onSubmit,
    forceType,
    forceClientId,
    lockOnLoad,
  } = options ?? {};

  /** True while the user is editing the location on the map (between edit click and green check). Save should be disabled when true. */
  const [isLocationEditing, setIsLocationEditing] = useState(false);

  /**
   * Locks derived from a loaded SavedLocation (edit mode). Distinct
   * from the caller-supplied {@link forceType}/{@link forceClientId}
   * so create flows stay unaffected; both layers are OR'd into the
   * `isLocationTypeLocked` / `isClientLocked` flags below.
   */
  const [recordLock, setRecordLock] = useState<{
    type: 'own' | 'client';
    clientId?: Id;
  } | null>(null);

  const initialClientId = forceClientId ?? defaultValuesProp?.clientId;
  const initialLocationType: SavedLocationFormValues['locationType'] =
    forceType ?? (initialClientId ? 'client' : defaultValuesProp?.locationType ?? 'own');

  const form = useForm<SavedLocationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: defaultValuesProp?.name ?? '',
      location: defaultValuesProp?.location ?? { lat: 0, lng: 0 },
      clientId: initialClientId,
      contacts: (defaultValuesProp?.contacts ?? []).map(c => ({ _id: c._id })),
      locationType: initialLocationType,
      locationUrl: defaultValuesProp?.locationUrl,
      isAccurate: defaultValuesProp?.isAccurate ?? true,
    },
  });

  const setSavedLocation = useCallback(
    (savedLocation: SavedLocation) => {
      const next = savedLocationToFormValues(savedLocation);
      // Honor `forceType` even when loading an existing record so the
      // selector stays locked.
      if (forceType) {
        next.locationType = forceType;
        if (forceType === 'client' && forceClientId) {
          next.clientId = getObjectId(forceClientId);
        }
        if (forceType === 'own') {
          next.clientId = undefined;
        }
      }
      // When `lockOnLoad` is set (edit-mode flows), capture the
      // record's type + client as the lock target so the user can
      // only change contact + map pin + name afterwards. Independent
      // from `forceType` so a create-flow caller can still configure
      // its own lock without colliding.
      if (lockOnLoad) {
        setRecordLock({
          type: savedLocation.client ? 'client' : 'own',
          clientId: savedLocation.client?._id,
        });
      }
      form.reset(next);
    },
    [form.reset, forceType, forceClientId, lockOnLoad],
  );

  /** Reset to default/empty state (including location). Used when switching to edit mode or when savedLocationId changes so we don't show stale data. */
  const getResetValues = useCallback((): Partial<SavedLocationFormValues> => {
    return {
      name: '',
      contacts: [],
      locationType: forceType ?? 'own',
      location: defaultValuesProp?.location ?? { lat: 0, lng: 0 },
      clientId:
        forceType === 'own' || !initialClientId ? undefined : getObjectId(initialClientId),
      locationUrl: undefined,
      isAccurate: true,
    };
  }, [
    defaultValuesProp?.location,
    initialClientId,
    forceType,
  ]);

  const reset = useCallback(() => {
    form.reset(getResetValues());
    // Drop any record-derived lock so a subsequent create flow on
    // the same hook instance starts unlocked. Edit flows re-arm the
    // lock the next time `setSavedLocation` runs.
    setRecordLock(null);
  }, [form, getResetValues]);

  const isFormValid = useCallback(() => {
    const name = form.getValues('name');
    if (!name || name.trim().length === 0) return false;
    const contacts = form.getValues('contacts');
    if (!contacts || contacts.length === 0) return false;
    const location = form.getValues('location');
    if (!location) return false;
    if (form.getValues('locationType') === 'client' && !form.getValues('clientId'))
      return false;
    return true;
  }, [form]);

  const values = form.watch();

  const locationType = values.locationType;

  // * contacts
  const getContactForLocation = (
    contact: Contact,
  ): NonNullable<SavedLocationFormValues['contacts']>[number] => {
    return { _id: contact._id };
  };
  const handleContactsSelect = useCallback(
    (contacts: Contact[] | undefined) => {
      form.setValue('contacts', (contacts ?? []).map(getContactForLocation), {
        shouldValidate: false,
      });
    },
    [form],
  );
  const handleContactSave = useCallback(
    (contact: Contact) => {
      form.setValue(
        'contacts',
        [getContactForLocation(contact), ...(form.getValues('contacts') ?? [])],
        {
          shouldValidate: false,
        },
      );
    },
    [form],
  );

  //* client
  const handleClientsSelected = useCallback(
    (clients: Client[] | undefined) => {
      if (forceType === 'client') return; // locked
      if (recordLock?.type === 'client') return; // locked by loaded record
      form.setValue('clientId', clients?.[0]?._id ?? undefined);
    },
    [form, forceType, recordLock],
  );
  const handleClientSave = useCallback(
    (data: { client: Client }) => {
      if (forceType === 'client') return; // locked
      if (recordLock?.type === 'client') return; // locked by loaded record
      form.setValue('clientId', data.client._id);
    },
    [form, forceType, recordLock],
  );
  // * other
  const isContactSelectorDisabled = locationType === 'client' && !values.clientId;

  const onValidSubmit = (values: SavedLocationFormValues) => {
    onSubmit?.(values);
  };

  // * Google Maps link → pin
  const resolveGoogleUrl = useSavedLocationResolveGoogleUrlApi();
  const [locationUrlError, setLocationUrlError] = useState<string | undefined>();
  /**
   * The last URL we resolved. Blur fires on every focus change, so
   * without this the same link re-resolves (three redirect hops plus a
   * possible geocode) each time the user tabs through the form.
   */
  const lastResolvedUrl = useRef<string | undefined>(undefined);

  /**
   * Resolve the pasted Maps link and move the pin.
   *
   * A link is a user-picked pin, so a successful resolve also flips
   * `isAccurate` to `true` — that is the whole point of pasting one when
   * you cannot drag the map precisely. `name` is only filled in when the
   * user has not typed one, so we never overwrite their label.
   */
  const resolveLocationUrl = useCallback(async () => {
    const raw = form.getValues('locationUrl')?.trim();
    if (!raw) {
      setLocationUrlError(undefined);
      lastResolvedUrl.current = undefined;
      return;
    }

    const url = extractGoogleMapsUrl(raw);
    if (!url) {
      setLocationUrlError('invalid');
      return;
    }
    if (url === lastResolvedUrl.current) return;

    setLocationUrlError(undefined);
    try {
      const resolved = await resolveGoogleUrl.mutateAsync({ url });
      lastResolvedUrl.current = url;
      form.setValue('location', { lat: resolved.lat, lng: resolved.lng });
      form.setValue('isAccurate', true);
      if (resolved.name && !form.getValues('name')?.trim()) {
        form.setValue('name', resolved.name);
      }
    } catch {
      setLocationUrlError('unresolvable');
    }
  }, [form, resolveGoogleUrl]);

  /** Call when the user enters location edit mode (edit icon on card or auto-enter). Disables Save until onLocationEditEnd. */
  const onLocationEditStart = useCallback(() => {
    setIsLocationEditing(true);
  }, []);

  /** Call when location edit ends (green check, cancel, or after save). When location is passed, updates form and re-enables Save; otherwise just re-enables Save. */
  const onLocationEditEnd = useCallback(
    (location?: MapLocationData) => {
      if (location) {
        form.setValue('location', location.cords);
      }
      setIsLocationEditing(false);
    },
    [form],
  );

  return {
    form,
    setValues: form.setValue,
    setSavedLocation,
    values,
    handleClientsSelected,
    handleContactsSelect,
    isContactSelectorDisabled,
    isFormValid,
    handleContactSave,
    reset,
    onValidSubmit,
    handleClientSave,
    isLocationEditing,
    onLocationEditStart,
    onLocationEditEnd,
    /** Resolve the pasted Google Maps link into the map pin. Call on blur. */
    resolveLocationUrl,
    /** `true` while the pasted link is being unfurled server-side. */
    isResolvingLocationUrl: resolveGoogleUrl.isPending,
    /** `'invalid'` (not a Maps URL) or `'unresolvable'` (Google gave us nothing). */
    locationUrlError,
    /** Whether the type selector should be hidden / locked. */
    isLocationTypeLocked: !!forceType || !!recordLock,
    /** Whether the client selector should be hidden / locked. */
    isClientLocked: forceType === 'client' || recordLock?.type === 'client',
  };
};
